import {useCallback, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useFormikContext} from 'formik';
import {useSelector} from 'react-redux';
import {getPriceGroupsByPackage, findPriceGroupByRange, createPriceGroup, updatePriceGroup} from '../../../../../../../redux/subscription/subscriptionActions';
import {bounceMessage} from '../../../../../../../layout/userMessages/UserMessage';
import {TShow} from '../../../../../../../component/prompotionPopup/PromotionPopup';
import { SubscriptionFormValues } from '../../../createSubscriptionForm.types';
import {XHR_LOADING_STATE} from '../../../../../../../assets/constants/constants';
import { TState } from '../../../../../../../redux/store.type';

const PRICE_RANGE_ALREADY_EXISTS = 'PRICE_RANGE_ALREADY_EXISTS';

export const usePriceGroups = (priceGroupPopShow: TShow) => {
    const {t} = useTranslation('subscriptions');
    const {values, setFieldValue} = useFormikContext<SubscriptionFormValues>();
    const priceGroups = useSelector((state: TState) => state.subscription.priceGroups || []);
    const getPriceGroupsByPackageXHR = useSelector((state: TState) => state.subscription.getPriceGroupsByPackageXHR);

    useEffect(() => {
        if(!getPriceGroupsByPackageXHR) {
            getPriceGroupsByPackage();
        }
    }, []);

    const addPriceGroupToForm = useCallback((priceGroupId: number) => {
        const currentPriceGroupIds = values.priceGroupIds || [];
        if(!currentPriceGroupIds.includes(priceGroupId)) {
            setFieldValue('priceGroupIds', [...currentPriceGroupIds, priceGroupId]);
        }
    }, [values.priceGroupIds, setFieldValue]);

    const handleExistingPriceGroup = useCallback(async (
        existingGroupId: number,
        existingGroupName: string,
        affectedCount: number,
        newName: string
    ) => {
        const existsMessage = t('priceGroupExistsMessage', {groupName: existingGroupName});
        const warningMessage = t('priceGroupUpdateWarningText');
        const affectedCountMessage = affectedCount > 0 ? t('affectedPlansCount', {count: affectedCount}) : '';
        const message = `${existsMessage}\n\n${warningMessage}${affectedCount > 0 ? `\n\n${affectedCountMessage}` : ''}`;

        bounceMessage(
            message,
            'choice',
            {
                text: t('continue'),
                onClick: async () => {
                    await updatePriceGroup(existingGroupId, {title: newName});
                    addPriceGroupToForm(existingGroupId);
                    priceGroupPopShow.close();
                }
            },
            {
                text: t('cancel')
            }
        );
    }, [t, addPriceGroupToForm, priceGroupPopShow]);

    const handleCreateNewPriceGroup = useCallback(async (
        name: string,
        minPrice: number,
        maxPrice: number
    ) => {
        const res = await createPriceGroup({title: name, minPrice, maxPrice});

        // Check if there's a conflict (server returns 200 with conflict: true)
        if(res?.conflict && res?.errorCode === PRICE_RANGE_ALREADY_EXISTS) {
            const existingGroupId = res.priceGroup?.spgId;
            const existingGroupName = res.existingGroup?.title || res.priceGroup?.title || t('pricingGroup');
            const affectedCount = res.affectedPlansCount || 0;

            if(existingGroupId) {
                await handleExistingPriceGroup(
                    existingGroupId,
                    existingGroupName,
                    affectedCount,
                    name
                );
            }
        } else if(res?.priceGroup) {
            // Successfully created new price group
            addPriceGroupToForm(res.priceGroup.spgId);
            priceGroupPopShow.close();
        }
    }, [t, addPriceGroupToForm, priceGroupPopShow, handleExistingPriceGroup]);

    const handleSavePriceGroup = useCallback(async (priceGroupValues: {name: string; startPrice: string; endPrice: string}) => {
        const minPrice = parseFloat(priceGroupValues.startPrice);
        const maxPrice = parseFloat(priceGroupValues.endPrice);

        try {
            const res = await findPriceGroupByRange(minPrice, maxPrice);
            if(res?.priceGroup) {
                const existingGroupId = res.priceGroup.spgId;
                const existingGroupName = res.existingGroup?.title || res.priceGroup.title || t('pricingGroup');
                const affectedCount = res.affectedPlansCount || 0;

                await handleExistingPriceGroup(
                    existingGroupId,
                    existingGroupName,
                    affectedCount,
                    priceGroupValues.name
                );
            } else {
                await handleCreateNewPriceGroup(priceGroupValues.name, minPrice, maxPrice);
            }
        } catch (error) {
            console.error('Error checking price group range:', error);
        }
    }, [t, handleExistingPriceGroup, handleCreateNewPriceGroup]);

    return {
        priceGroups,
        handleSavePriceGroup
    };
};

